import { inject } from "fw";
import { handle } from "fw-state";
import { LogoutAction, StartAction } from "./actions";
import { once } from "helpers/once";
import { AppsyncResultsRepository } from "network/ats-appsync-repository";

import { AppSyncDetailsTabs, AppSyncDetail, REASON_TYPE, DOCUMENT_STATUS, AppSyncFilter } from "models/monitor-appsync-results";
import { GridStore, ResetAction, RefreshAction } from "./components/grid";

const CACHE_KEY = "ats-appsync-details-store";
const STATUS_MAPPING = {
    [AppSyncDetailsTabs.AtsMissingApps]: `AND reasonType:${REASON_TYPE.MissingFromAts}`,
    [AppSyncDetailsTabs.CasMissingApps]: `AND reasonType:${REASON_TYPE.MissingFromCas}`,
    [AppSyncDetailsTabs.DeliveredApps]: "AND isDelivered:true",
    [AppSyncDetailsTabs.DeliveredDocs]: `AND documents:(documents.status:${DOCUMENT_STATUS.Delivered})`
};

export class AppSyncDetailsEnsureAction {}
export class AppSyncDetailsRefreshAction {}
export class AppSyncDetailsResetAction {}
export class AppSyncDetailsNextPageAction {}
export class AppSyncDetailsPreviousPageAction {}
export class AppSyncDetailsFetchListAction<Filter> {
    constructor(
        public filter: Filter = null,
        public search: string = null,
        public sort: string = ""
    ) {}
}

@inject
export class AppSyncDetailsStore extends GridStore<AppSyncDetail, AppSyncFilter> {
    constructor(private network: AppsyncResultsRepository) {
        super(CACHE_KEY);
    }

    @handle(StartAction)
    private startAction(action: StartAction) {
        this.handleStartAction(action);
    }

    @handle(LogoutAction)
    private logoutAction() {
        this.handleLogoutAction();
    }

    @handle(AppSyncDetailsEnsureAction)
    private async handleAtsOrganizationEnsureAction() {
        await once(CACHE_KEY, async () => {
            if (this.state.loaded) return;
            this.restoreState();
            this.loadPageList();
        });
    }

    @handle(AppSyncDetailsRefreshAction)
    private refreshAction(action: RefreshAction) {
        this.handleRefreshAction(action, () => {
            this.loadPageList();
        });
    }

    @handle(AppSyncDetailsResetAction)
    private resetAction(action: ResetAction) {
        this.handleResetAction(action, () => {
            this.loadPageList();
        });
    }

    @handle(AppSyncDetailsFetchListAction)
    private fetchListAction(action: AppSyncDetailsFetchListAction<AppSyncFilter>) {
        this.handleFetchList(action, () => {
            this.loadPageList();
        });
    }

    @handle(AppSyncDetailsNextPageAction)
    private nextPageAction(action: AppSyncDetailsNextPageAction) {
        this.handleNextPageAction(action, async (nextPageToken) => {
            await this.loadPageData(null, nextPageToken);
        });
    }

    @handle(AppSyncDetailsPreviousPageAction)
    private previousPageAction(action: AppSyncDetailsPreviousPageAction) {
        this.handlePreviousPage(action, async (previousPageToken) => {
            await this.loadPageData(previousPageToken, null);
        });
    }

    private async loadPageList() {
        const { currentPreviousPageToken, currentNextPageToken } = this.state;
        await this.loadPageData(currentPreviousPageToken, currentNextPageToken);
    }

    private async loadPageData(previousPageToken: string, nextPageToken: string) {
        try {
            const resp = await this.network.appSyncResultDetailsList(
                this.getWholeFilter(),
                null,
                this.getOrgFilter(),
                null,
                this.state.pageSize,
                previousPageToken,
                nextPageToken,
                null,
                null
            );

            this.setState((state) => ({
                ...state,
                loaded: true,
                previousPageToken: resp.PreviousPageToken,
                nextPageToken: resp.NextPageToken,
                currentPreviousPageToken: previousPageToken,
                currentNextPageToken: nextPageToken,
                list: resp.Results,
                total: resp.Total
            }));
        } catch (err) {
            this.setState((state) => ({ ...state, loaded: true, errorLoading: true }));
        }
    }

    public getWholeFilter(withStatus = true): string {
        const { filter } = this.state;
        return `appSyncResultId:${filter.appSyncResultId} ${STATUS_MAPPING[filter.tabType]}`;
    }

    private getOrgFilter() {
        const { tags, type } = this.state.orgFilter;

        if (type === null && !tags?.length) return null;

        const filterArgs = [];
        if (type !== null) filterArgs.push(`metaData.type:${type}`);
        if (tags?.length) filterArgs.push(...tags.map((tag) => `tags:("${tag}")`));

        return filterArgs.join(" AND ");
    }
}
